import { useEffect } from "react";
import styles from "./CreateCommunityPage.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import useDashboardStore from "../../store/dashboardStore";
import { toast } from "react-toastify";

// Components
import CommunityImageUpload from "../../components/Community/CommunityImageUpload";
import CommunityBasicInfo from "../../components/Community/CommunityBasicInfo";
import SectionsManager from "../../components/Community/SectionsManager";
import PageSectionsManager from "../../components/PageSectionsManager";

// Hooks
import { useCommunityForm } from "../../hooks/useCommunityForm";
import { useSections } from "../../hooks/useSections";
import {
  usePageSections,
  sectionsFromApi,
  buildSectionsPayload,
} from "../../hooks/usePageSections";

const CreateCommunityPage = () => {
  const { setIsAllCommunityScreen, editCommunityId } = useDashboardStore();

  // Custom hooks
  const communityFormState = useCommunityForm(editCommunityId);
  const sectionsState = useSections();
  const pageSections = usePageSections();

  // Load community content + sections when editing.
  useEffect(() => {
    const loadCommunityData = async () => {
      if (editCommunityId === null) return;
      try {
        const communityData = await communityFormState.prefillFormForEdit();
        if (!communityData) return;

        sectionsState.setSelectedSectionIds(communityData.section_ids || []);

        const compat =
          communityData.sections && communityData.sections.length
            ? { sections: communityData.sections }
            : {
                content: {
                  id: null,
                  content_title: communityData.content_title,
                  content_primary_text: communityData.content_primary_text,
                  content_secondary_text: communityData.content_secondary_text,
                  content_banner_web: communityData.content_banner_web,
                  content_banner_app: communityData.content_banner_app,
                  content_image_web: communityData.content_image_web,
                  content_image_app: communityData.content_image_app,
                  video_tabs: communityData.video_tabs || [],
                },
              };
        pageSections.setSections(sectionsFromApi(compat));
        pageSections.setActiveSectionIndex(0);
        pageSections.setVideoCoverFiles([]);
      } catch (err) {
        console.error("Failed to load community data", err);
      }
    };

    loadCommunityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCommunityId]);

  const handleSubmit = async () => {
    if (communityFormState.isSubmitting) return;

    if (
      !communityFormState.communityName ||
      !communityFormState.themeColor ||
      !sectionsState.selectedSectionIds.length
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (editCommunityId === null) {
      if (!communityFormState.bannerFile || !communityFormState.iconFile) {
        toast.error("Please upload both banner and icon images.");
        return;
      }
    }

    communityFormState.setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("name", communityFormState.communityName);
      formData.append("theme_color", communityFormState.themeColor);
      formData.append(
        "has_live_session",
        communityFormState.hasLiveSession === true ? true : false,
      );
      formData.append(
        "section_ids",
        JSON.stringify(sectionsState.selectedSectionIds),
      );

      if (communityFormState.bannerFile)
        formData.append("banner", communityFormState.bannerFile);
      if (communityFormState.iconFile)
        formData.append("icon", communityFormState.iconFile);

      // Community's own academy title stays a page-level field.
      const communityTitle = communityFormState.content?.communityTitle;
      if (communityTitle) formData.append("community_title", communityTitle);

      const { sections, section_files, group_files, video_attachments } =
        buildSectionsPayload(pageSections.sections);

      formData.append("sections", JSON.stringify(sections));
      section_files.forEach((f) => formData.append("section_files", f));
      group_files.forEach((f) => formData.append("group_files", f));
      video_attachments.forEach((f) => formData.append("video_attachments", f));
      pageSections.videoCoverFiles.forEach((f) =>
        formData.append("video_covers", f),
      );

      const { data } =
        editCommunityId !== null
          ? await api.put(
              ENDPOINTS.UPDATE_COMMUNITY_BY_ID(editCommunityId),
              formData,
            )
          : await api.post(ENDPOINTS.CREATE_COMMUNITY, formData);

      if (data.success) {
        toast.success(
          data.message ||
            `Community ${editCommunityId ? "updated" : "created"} successfully`,
        );
        setIsAllCommunityScreen();
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        `Error ${editCommunityId ? "updating" : "creating"} community`,
      );
    } finally {
      communityFormState.setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      {/* App Bar */}
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>
          {editCommunityId === null ? "Create" : "Update"} Community
        </h2>
      </div>

      <div className={styles.bodyContainer}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          <CommunityImageUpload
            bannerPreview={communityFormState.bannerPreview}
            iconPreview={communityFormState.iconPreview}
            handleBannerUpload={communityFormState.handleBannerUpload}
            handleIconUpload={communityFormState.handleIconUpload}
            isSubmitting={communityFormState.isSubmitting}
          />

          <CommunityBasicInfo
            communityName={communityFormState.communityName}
            setCommunityName={communityFormState.setCommunityName}
            themeColor={communityFormState.themeColor}
            setThemeColor={communityFormState.setThemeColor}
            hasLiveSession={communityFormState.hasLiveSession}
            setHasLiveSession={communityFormState.setHasLiveSession}
            isSubmitting={communityFormState.isSubmitting}
          />

          <SectionsManager
            filteredSections={sectionsState.filteredSections}
            searchText={sectionsState.searchText}
            setSearchText={sectionsState.setSearchText}
            newSection={sectionsState.newSection}
            setNewSection={sectionsState.setNewSection}
            selectedSectionIds={sectionsState.selectedSectionIds}
            editingSectionId={sectionsState.editingSectionId}
            editingSectionTitle={sectionsState.editingSectionTitle}
            setEditingSectionTitle={sectionsState.setEditingSectionTitle}
            isCreatingSection={sectionsState.isCreatingSection}
            isRenamingSection={sectionsState.isRenamingSection}
            deletingSectionId={sectionsState.deletingSectionId}
            isSubmitting={communityFormState.isSubmitting}
            handleCheckboxChange={sectionsState.handleCheckboxChange}
            handleCreateSection={sectionsState.handleCreateSection}
            handleStartEdit={sectionsState.handleStartEdit}
            handleCancelEdit={sectionsState.handleCancelEdit}
            handleSaveRename={sectionsState.handleSaveRename}
            handleDeleteSection={sectionsState.handleDeleteSection}
          />
        </div>

        {/* Right Section */}
        <div className={styles.rightSection}>
          <div className={styles.sectionsCard}>
            <div className={styles.rowInputGroup}>
              <label className={styles.inlineLabel}>Academy / Content Title</label>
              <input
                type="text"
                value={communityFormState.content?.communityTitle || ""}
                onChange={(e) =>
                  communityFormState.setContent({
                    ...communityFormState.content,
                    communityTitle: e.target.value,
                  })
                }
                className={styles.textInput}
                disabled={communityFormState.isSubmitting}
              />
            </div>
          </div>

          <PageSectionsManager
            pageSections={pageSections}
            isSubmitting={communityFormState.isSubmitting}
          />

          {/* Submit Button */}
          <button
            className={`${styles.submitButton} ${
              communityFormState.isSubmitting ? styles.submitting : ""
            }`}
            onClick={handleSubmit}
            disabled={communityFormState.isSubmitting}
          >
            {communityFormState.isSubmitting ? (
              <>
                <span>
                  {editCommunityId === null ? "Creating..." : "Updating..."}
                </span>
                <span className={styles.submitIcon}>⟳</span>
              </>
            ) : (
              <>
                <span>Submit Community</span>
                <span className={styles.submitIcon}>＋</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCommunityPage;
